// Helpers for the Firestore-backed blog: translation selection, search, related, etc.

export const DEFAULT_LANG = "ar";
export const RTL_LANGS = ["ar"];

export function dirFor(lang) {
  return RTL_LANGS.includes(lang) ? "rtl" : "ltr";
}

// Pick the requested translation, falling back to the article's default language,
// then to whatever translation exists. Returns { lang, tr } or null.
export function getTranslation(article, lang) {
  if (!article || !article.translations) return null;
  const t = article.translations;
  const fallback = article.defaultLang || DEFAULT_LANG;
  if (t[lang]) return { lang, tr: t[lang] };
  if (t[fallback]) return { lang: fallback, tr: t[fallback] };
  const first = Object.keys(t)[0];
  return first ? { lang: first, tr: t[first] } : null;
}

// Languages an article is actually available in.
export function availableLangs(article) {
  return article && article.translations ? Object.keys(article.translations) : [];
}

// Normalize for case/diacritic-insensitive matching.
export function normalize(str = "") {
  return String(str)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip Latin combining marks
    .trim();
}

// Build a searchable blob from a translation + article-level fields.
export function buildSearchText(article, tr) {
  const parts = [];
  if (tr) {
    parts.push(tr.title, tr.excerpt);
    (tr.content || []).forEach((b) => {
      if (b.type === "paragraph" || b.type === "heading") parts.push(b.text);
      if (b.type === "list") parts.push((b.items || []).join(" "));
      if (b.type === "quran") parts.push(b.surah, b.translation);
      if (b.type === "hadith") parts.push(b.collection, b.translation);
    });
  }
  if (article) {
    parts.push(article.category, article.author);
    parts.push((article.tags || []).join(" "));
  }
  return normalize(parts.filter(Boolean).join(" "));
}

// True if the article (in the given language) matches the query string.
export function matchesQuery(article, lang, q) {
  const query = normalize(q);
  if (!query) return true;
  const picked = getTranslation(article, lang);
  const haystack =
    (picked && picked.tr && picked.tr.searchText) ||
    buildSearchText(article, picked && picked.tr);
  return normalize(haystack).includes(query);
}

const WORDS_PER_MIN = 200;

// Minutes to read; uses stored value if present, else estimates from blocks.
export function estimateReadingTime(tr) {
  if (!tr) return 1;
  if (tr.readingTime) return tr.readingTime;
  let words = 0;
  (tr.content || []).forEach((b) => {
    if (b.type === "paragraph" || b.type === "heading") {
      words += String(b.text || "").split(/\s+/).length;
    } else if (b.type === "list") {
      words += (b.items || []).join(" ").split(/\s+/).length;
    } else if (b.type === "quran" || b.type === "hadith") {
      words += String(b.translation || "").split(/\s+/).length;
    }
  });
  return Math.max(1, Math.round(words / WORDS_PER_MIN));
}

// Unique categories with counts, in the selected language.
export function deriveCategories(articles, lang) {
  const map = new Map();
  (articles || []).forEach((a) => {
    if (!a.category) return;
    map.set(a.category, (map.get(a.category) || 0) + 1);
  });
  return Array.from(map, ([slug, count]) => ({ slug, count }));
}

// Unique tags with counts.
export function deriveTags(articles) {
  const set = new Set();
  (articles || []).forEach((a) => (a.tags || []).forEach((t) => set.add(t)));
  return Array.from(set);
}

// Same-category articles (fallback: shared tags), excluding the current one.
export function relatedArticles(articles, current, lang, n = 3) {
  if (!current) return [];
  const others = (articles || []).filter((a) => a.id !== current.id);
  const sameCat = others.filter((a) => a.category && a.category === current.category);
  if (sameCat.length >= n) return sameCat.slice(0, n);
  const curTags = new Set(current.tags || []);
  const byTag = others
    .filter((a) => !sameCat.includes(a) && (a.tags || []).some((t) => curTags.has(t)));
  return [...sameCat, ...byTag, ...others]
    .filter((a, i, arr) => arr.indexOf(a) === i)
    .slice(0, n);
}

// Find an article by any translation's slug.
export function findBySlug(articles, slug) {
  if (!slug) return null;
  return (
    (articles || []).find((a) =>
      Object.values(a.translations || {}).some((t) => t && t.slug === slug)
    ) || null
  );
}

// Format a Firestore Timestamp | Date | ISO string for display.
export function formatDate(value, lang = DEFAULT_LANG) {
  if (!value) return "";
  let d;
  if (typeof value === "object" && typeof value.toDate === "function") d = value.toDate();
  else d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const locale = lang === "ar" ? "ar-MA" : lang === "fr" ? "fr-FR" : "en-US";
  return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}
